import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, Droplets, Sparkles, ShieldCheck } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1581182815808-b6eb627a8798?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHx3b21hbiUyMGdsb3dpbmclMjBza2luJTIwbmF0dXJhbCUyMGJlYXV0eXxlbnwwfHx8fDE3ODU0MDU5NjR8MA&ixlib=rb-4.1.0&q=85";
const LEAF_IMAGE =
  "https://images.unsplash.com/photo-1580133318324-f2f76d987dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxncmVlbiUyMGxlYWYlMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3ODU0MDU5NzR8MA&ixlib=rb-4.1.0&q=85";
const COCONUT_IMAGE =
  "https://images.unsplash.com/photo-1560769680-ba2f3767c785?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjb2NvbnV0JTIwc2hlbGwlMjBoYWxmfGVufDB8fHx8MTc4NTQwNTk3M3ww&ixlib=rb-4.1.0&q=85";
const PORTRAIT_IMAGE =
  "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRpdmVyc2UlMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTQwNTk3NHww&ixlib=rb-4.1.0&q=85";

const RIBBON_ITEMS = [
  "COLD-PRESSED",
  "100% ORGANIC",
  "CRUELTY-FREE",
  "GLASS-BOTTLED",
  "SMALL-BATCH",
  "AYURVEDIC",
];

const testimonials = [
  {
    name: "Priya S.",
    quote:
      "My hair has never felt this healthy. The coconut oil sinks in without leaving any residue.",
  },
  {
    name: "Meera K.",
    quote:
      "The moisturising cream is a daily ritual now — my skin looks lit-from-within.",
  },
  {
    name: "Aisha R.",
    quote:
      "Tried the anti-lice shampoo on my daughter — worked in a single wash. So gentle.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1 relative z-10">
            <p className="overline">Est. 2026 · Small-batch Botanicals</p>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#1e392a]">
              Ritual, rooted<br />
              in <em className="text-[#c36a4f]">botanics.</em>
            </h1>
            <p className="mt-6 text-base text-[#1a1b1a]/70 max-w-md leading-relaxed">
              Cold-pressed oils, ayurvedic shampoos and whipped creams — made in
              small batches, bottled in glass, and never tested on animals.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/shop">
                <Button
                  data-testid="hero-shop-btn"
                  className="h-12 px-8 rounded-full bg-[#1e392a] hover:bg-[#14251b] text-white text-sm tracking-wide"
                >
                  Shop the collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  data-testid="hero-about-btn"
                  variant="ghost"
                  className="h-12 px-8 rounded-full text-[#1e392a] hover:bg-[#efeadd] text-sm tracking-wide"
                >
                  Our story
                </Button>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <p className="font-serif text-3xl text-[#1e392a]">12k+</p>
                <p className="text-xs text-[#8a8d86] uppercase tracking-widest mt-1">Bottles poured</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-[#1e392a]">98%</p>
                <p className="text-xs text-[#8a8d86] uppercase tracking-widest mt-1">Would repurchase</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-[#1e392a]">0</p>
                <p className="text-xs text-[#8a8d86] uppercase tracking-widest mt-1">Animal tests</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden">
              <img src={HERO_IMAGE} alt="Botanical beauty" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -left-8 hidden lg:block bg-[#f9f6f0] border border-[#e6e2d8] p-6 rounded-md max-w-[240px] shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <p className="overline">Editor's Pick</p>
              <p className="font-serif text-xl text-[#1e392a] mt-1">Botanical Cream</p>
              <p className="text-xs text-[#8a8d86] mt-2">24-hour hydration with shea and rose.</p>
            </div>
          </div>
        </div>
      </section>

      {/* RIBBON */}
      <section className="border-y border-[#e6e2d8] bg-[#1e392a] text-[#f9f6f0] py-5 overflow-hidden">
        <div className="marquee-track">
          {[...RIBBON_ITEMS, ...RIBBON_ITEMS].map((t, i) => (
            <span key={i} className="text-xs tracking-[0.32em] font-medium flex items-center gap-8">
              {t}
              <span className="text-[#c36a4f]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <p className="overline">The Essentials</p>
            <h2 className="mt-2 font-serif text-4xl sm:text-5xl text-[#1e392a] tracking-tight">
              A capsule of everyday rituals.
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="view-all-shop"
            className="mt-4 md:mt-0 text-sm text-[#c36a4f] hover:underline flex items-center gap-1"
          >
            View all products <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* BENTO INGREDIENTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:auto-rows-[220px]">
          <div className="md:col-span-4 md:row-span-2 relative overflow-hidden rounded-md bg-[#1e392a] text-[#f9f6f0] p-10 flex flex-col justify-end">
            <img src={LEAF_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="relative z-10 max-w-md">
              <p className="overline text-[#c36a4f]">Sourced</p>
              <h3 className="font-serif text-4xl mt-2 leading-tight">
                From wild neem groves to your washroom shelf.
              </h3>
              <p className="mt-4 text-sm text-[#f9f6f0]/80">
                Every ingredient is traceable to a farmer partner. No middlemen, no
                fillers, no shortcuts.
              </p>
            </div>
          </div>
          <div className="md:col-span-2 bg-white border border-[#e6e2d8] rounded-md p-8">
            <Leaf className="h-6 w-6 text-[#c36a4f]" strokeWidth={1.5} />
            <p className="font-serif text-2xl text-[#1e392a] mt-4">Cold-pressed</p>
            <p className="text-sm text-[#8a8d86] mt-2">
              Pressed below 40°C to retain every drop of active nutrient.
            </p>
          </div>
          <div className="md:col-span-2 relative overflow-hidden rounded-md">
            <img src={COCONUT_IMAGE} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-3 bg-white border border-[#e6e2d8] rounded-md p-8">
            <Droplets className="h-6 w-6 text-[#c36a4f]" strokeWidth={1.5} />
            <p className="font-serif text-2xl text-[#1e392a] mt-4">Glass bottled</p>
            <p className="text-sm text-[#8a8d86] mt-2">
              We reject plastic packaging so your rituals leave no residue.
            </p>
          </div>
          <div className="md:col-span-3 bg-[#c36a4f]/10 border border-[#c36a4f]/20 rounded-md p-8">
            <ShieldCheck className="h-6 w-6 text-[#c36a4f]" strokeWidth={1.5} />
            <p className="font-serif text-2xl text-[#1e392a] mt-4">Dermatologically tested</p>
            <p className="text-sm text-[#8a8d86] mt-2">
              Independently verified for sensitive skin — because trust matters.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#efeadd] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="overline text-center">Loved by</p>
          <h2 className="mt-2 font-serif text-4xl sm:text-5xl text-[#1e392a] text-center">
            What our community says.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white p-8 rounded-md border border-[#e6e2d8]"
              >
                <Sparkles className="h-5 w-5 text-[#c36a4f] mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl text-[#1e392a] leading-snug">"{t.quote}"</p>
                <p className="mt-6 text-xs uppercase tracking-widest text-[#8a8d86]">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="overline">Journal</p>
        <h2 className="mt-2 font-serif text-4xl sm:text-5xl text-[#1e392a]">
          Rituals in your inbox.
        </h2>
        <p className="mt-4 text-sm text-[#8a8d86] max-w-md mx-auto">
          Slow beauty, ingredient stories and 10% off your first order.
        </p>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="mt-8 flex max-w-md mx-auto gap-2"
        >
          <input
            type="email"
            required
            data-testid="newsletter-email-input"
            placeholder="your@email.com"
            className="flex-1 h-12 px-4 rounded-full bg-white border border-[#e6e2d8] text-sm focus:outline-none focus:border-[#1e392a]"
          />
          <Button
            data-testid="newsletter-submit-btn"
            className="h-12 px-8 rounded-full bg-[#1e392a] hover:bg-[#14251b] text-white"
          >
            Subscribe
          </Button>
        </form>
      </section>
    </div>
  );
}
